/**
 * NavBar
 *
 * Renders the admin navigation menu, filtering items based on the current
 * user's role. Route-access rules are derived from the centralized
 * ROLE_ROUTE_ACCESS matrix in src/permissions/index.ts so that
 * nav visibility stays in sync with route guards automatically.
 */
import { useLocation, useNavigate } from "react-router-dom";
import { Container, Item } from "./styles";
import isMobile from "is-mobile";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useEffect, useMemo, useState } from "react";
import { usePermission } from "../../../hooks/use-permission";
import { ADMIN_NAV_ITEMS } from "../admin-nav/items";

// Uma lista só para o menu lateral e para a gaveta do celular.
const navItems = ADMIN_NAV_ITEMS;

export const NavBar: React.FC = () => {
  const navigate = useNavigate();
  const { canAccess, hasRole } = usePermission();

  const location = useLocation();
  const [activePath, setActivePath] = useState(location.pathname);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const filteredNavItems = useMemo(
    () => navItems.filter((item) => {
      if (!canAccess(item.path)) return false;
      if (item.path === "/backlinks-dashboard" && hasRole("ADMIN")) return false;
      return true;
    }),
    [canAccess, hasRole],
  );

  useEffect(() => {
    const currentNavItem = filteredNavItems.find((item) =>
      location.pathname.startsWith(item.path),
    );
    if (currentNavItem) {
      setActivePath(currentNavItem.path);
    } else {
      setActivePath(location.pathname);
    }
  }, [location.pathname, filteredNavItems]);

  const handleNavigate = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
  };

  if (isMobile()) {
    return (
      <>
        <IconButton
          onClick={() => setDrawerOpen(true)}
          sx={{
            marginRight: "8vw",
            zIndex: 1101,
          }}
        >
          <MenuIcon sx={{ fontSize: "30px" }} />
        </IconButton>
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          sx={{ zIndex: 1300 }}
        >
          <Box
            sx={{ width: 250 }}
            role="presentation"
            onClick={() => setDrawerOpen(false)}
            onKeyDown={() => setDrawerOpen(false)}
          >
            <List>
              {filteredNavItems.map((item) => (
                <ListItem key={item.path} disablePadding>
                  <ListItemButton
                    selected={activePath === item.path}
                    onClick={() => handleNavigate(item.path)}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </Drawer>
      </>
    );
  }

  return (
    <Container>
      {filteredNavItems.map((item) => (
        <Item
          key={item.path}
          $isActive={location.pathname.startsWith(item.path)}
          onClick={() => handleNavigate(item.path)}
        >
          {item.icon}
          <span>{item.label}</span>
        </Item>
      ))}
    </Container>
  );
};
